import { formatNumber } from '../../../utils/numberFormat';

/**
 * Pure: the failure list's heading, stating its period and reconciling with the
 * Failed actions tile: "Why actions failed · 7 Sep – 11 Oct, same weeks as the
 * Failed actions tile: 5 · 2 of them last full week". Exported for tests.
 */
export function failureHeading(range: string, periodFailures: number, listed: number, reasons: number, lastWeek: number): string {
  const top = listed < periodFailures ? ` (top ${reasons} reasons: ${formatNumber(listed)})` : '';
  return `Why actions failed · ${range}, same weeks as the Failed actions tile: ${formatNumber(periodFailures)}${top} · ${formatNumber(lastWeek)} of them last full week · lemlist’s own message where it gave one, most frequent first`;
}

/**
 * lemlist's failure messages in plain words. lemlist ends many with its own
 * error code in brackets ("…only inmail is possible (missing-compose-action)");
 * a known code gets a sentence, any other code is dropped from the text.
 * Mirrors plainLemlistReason in packages/api/src/modules/outbound/activity-types.ts.
 */
const LEMLIST_CODE_TEXT: Record<string, string> = {
  'missing-compose-action': 'LinkedIn message not sent: the lead is not a 1st-degree connection, so only an InMail was possible',
};

export function plainFailureReason(reason: string): string {
  const m = /^(.*?)\s*\(([a-z0-9]+(?:-[a-z0-9]+)+)\)\s*$/i.exec(reason.trim());
  if (!m) return reason.trim();
  return LEMLIST_CODE_TEXT[m[2].toLowerCase()] ?? (m[1].trim() || reason.trim());
}

/**
 * Pure: failure reasons summed over the given weeks, most frequent first, so
 * the list covers the same full weeks as the Failed actions tile (12 Oct: the
 * tile said 5 over the period while the list showed last week's 2). Exported for tests.
 */
export function periodFailureReasons(
  reasons: ReadonlyArray<{ week: string; reason: string; count: number }>,
  weeks: readonly string[],
): Array<{ reason: string; count: number }> {
  const inWeeks = new Set(weeks);
  const byReason = new Map<string, number>();
  for (const r of reasons) {
    if (!inWeeks.has(r.week)) continue;
    const reason = plainFailureReason(r.reason);
    byReason.set(reason, (byReason.get(reason) ?? 0) + r.count);
  }
  return [...byReason.entries()]
    .map(([reason, count]) => ({ reason, count }))
    .sort((a, b) => b.count - a.count || a.reason.localeCompare(b.reason));
}

/**
 * Pure: reps with no touches (lemlist or Apollo) in the week, folded into one sentence:
 * "Patrick, Andrew: no touches last full week · Nadia: no lemlist account".
 * First names, as the team says them. Null when everyone worked. Exported for tests.
 */
export function silentRepsLine(reps: ReadonlyArray<{ name: string; repKeys: readonly string[] }>): string | null {
  const first = (name: string) => name.split(' ')[0] || name;
  const noTouches = reps.filter((r) => r.repKeys.length > 0).map((r) => first(r.name));
  const noAccount = reps.filter((r) => r.repKeys.length === 0).map((r) => first(r.name));
  const parts = [
    noTouches.length > 0 && `${noTouches.join(', ')}: no touches last full week`,
    noAccount.length > 0 && `${noAccount.join(', ')}: no lemlist account`,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : null;
}
